import { useMemo } from 'react';
import { withNamespaces } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
    Alert,
    Col,
    Container,
    Row,
    Spinner,
} from 'reactstrap';

import Navigator from 'src/components/Navigator';

import Compose from './components/compose';
import EmailSidebar from './components/email-sidebar';
import FolderPlaceholder from './components/folder-placeholder';
import MessageDetail from './components/message-detail';
import useMailboxMessage from './components/message-detail/hooks/useMailboxMessage';
import MessageFolder from './components/message-folder';
import {
    getMailboxConversationSubject,
    getMailboxInboxPath,
    getMessageSubject,
    hasMailboxThread,
} from './helpers';
import useMailboxBackPath from './hooks/useMailboxBackPath';
import useMailboxGuard from './hooks/useMailboxGuard';
import useMailboxRoute from './hooks/useMailboxRoute';
import useMailboxRoleAccess from 'src/modules/mailer/hooks/useMailboxRoleAccess';

const Mailer = ({
    t,
}) => {
    const {
        mailerEnabled,
        isChecking,
        shouldRenderMailbox,
    } = useMailboxGuard({ t });
    const { canComposeNew } = useMailboxRoleAccess();
    const mailboxRoute = useMailboxRoute();
    const location = useLocation();
    const {
        backPath,
        backFolderTitleKey,
    } = useMailboxBackPath();

    const isCompose = mailboxRoute.view === 'compose';
    const isDetail = mailboxRoute.view === 'detail';
    const isReply = isCompose && !!location.state?.mailboxReply;

    const { message } = useMailboxMessage({
        t,
        messageId: isDetail ? mailboxRoute.messageId : null,
    });

    const messageSubject = isDetail
        ? (hasMailboxThread(message)
            ? getMailboxConversationSubject(message)
            : getMessageSubject(message))
        : '';
    const pageTitle = isDetail
        ? (messageSubject || 'Message')
        : (isReply ? 'Reply' : mailboxRoute.titleKey);

    const breadCrumbs = useMemo(() => {
        const crumbs = [
            { title: 'SIP', link: '/' },
            { title: 'Mailbox', link: getMailboxInboxPath() },
        ];

        if (isCompose || isDetail) {
            crumbs.push({
                title: backFolderTitleKey || 'Inbox',
                link: backPath || getMailboxInboxPath(),
            });
            crumbs.push({
                title: isDetail
                    ? 'Detail'
                    : (isReply ? 'Reply' : 'Compose'),
                link: '#',
            });

            return crumbs;
        }

        crumbs.push({
            title: mailboxRoute.titleKey,
            link: '#',
        });

        return crumbs;
    }, [
        backFolderTitleKey,
        backPath,
        isCompose,
        isDetail,
        isReply,
        mailboxRoute.titleKey,
    ]);

    const renderContent = () => {
        if (isChecking || !shouldRenderMailbox) {
            return (
                <div className="text-center py-5">
                    <Spinner color="primary" />
                </div>
            );
        }

        if (isCompose) {
            if (!canComposeNew && !isReply) {
                return (
                    <Alert color="info">
                        {t('You can reply to emails linked to your reports from the case Emails tab.')}
                    </Alert>
                );
            }

            return <Compose />;
        }

        if (isDetail) {
            return (
                <MessageDetail
                    messageId={mailboxRoute.messageId}
                />
            );
        }

        if (mailboxRoute.view === 'folder' && mailboxRoute.folder === 'inbox') {
            return (
                <MessageFolder
                    key="inbox"
                    folder="inbox"
                    showFlagFilters
                    partyMode="from"
                />
            );
        }

        if (mailboxRoute.view === 'folder' && mailboxRoute.folder === 'sent') {
            return (
                <MessageFolder
                    key="sent"
                    folder="sent"
                    partyMode="to"
                />
            );
        }

        if (mailboxRoute.view === 'folder' && mailboxRoute.folder === 'starred') {
            return (
                <MessageFolder
                    key="starred"
                    folder="starred"
                    partyMode="from"
                />
            );
        }

        if (mailboxRoute.view === 'folder' && mailboxRoute.folder === 'trash') {
            return (
                <MessageFolder
                    key="trash"
                    folder="trash"
                    partyMode="from"
                />
            );
        }

        return <FolderPlaceholder folder={mailboxRoute.folder} />;
    };

    if (!mailerEnabled) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Alert color="warning">
                        {t('Please upgrade your plan to access this feature')}.
                    </Alert>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-content">
            <Container fluid>
                <Row>
                    <Col lg="12" className="mb-4">
                        <Navigator
                            pageTitle={pageTitle}
                            breadCrumbs={breadCrumbs}
                        />
                    </Col>
                </Row>

                <Row className="mailer-container">
                    <Col md="3" lg="2" sm="12">
                        <EmailSidebar
                            activeView={mailboxRoute.view}
                            activeFolder={mailboxRoute.folder}
                            enabled={shouldRenderMailbox}
                        />
                    </Col>
                    <Col md="9" lg="10" sm="12">
                        {renderContent()}
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default withNamespaces()(Mailer);
